import widgetProcessor, { type WidgetConfig } from 'Components/widgetProcessor';
import { logRuntimeError } from 'Diagnostics/runtimeLogger';
import type { AutocompleteTypes } from 'Models/autocompleteItem';
import type { PreselectionTreeLevel } from 'Models/facetRequest';
import type { FacetValueFull, PreselectionEntry } from 'Models/index';
import type { FitmentSearchCallbackConfig } from 'Models/uiConfig/fitmentSearchConfig.types';
import type { ProductRuntimeConfig } from 'Models/uiConfig/productConfig.types';
import type {
  AutocompleteRequestDefaults,
  ConfigMap,
  ParsedSearchRequest,
  PlatformAddItemsToCart,
  RuntimeConfigMap,
  RuntimeConfigValue,
  SearchRequestDefaults,
  SearchRequestDefaultsFactory,
} from 'Models/uiConfig/requestTypes';
import type { ServerModel } from 'Modules/serverApi/types';
import { unprocessedConfig } from 'Templates/templates';
import { type AnyWidget, widgetTypeNames } from 'Templates/widgets';

export type CustomFacetValueFilter = (
  value: FacetValueFull,
  opts: {
    filter: (v: FacetValueFull) => boolean;
    preselection: PreselectionTreeLevel[];
  },
) => boolean;

export type CustomMessageVisibleIf = (params: ConfigMap) => boolean;

export type CustomMessageConfig = ConfigMap<RuntimeConfigValue | CustomMessageVisibleIf> & {
  paramsKeys?: string[];
  visibleIf?: CustomMessageVisibleIf;
};

export type PageConfig = {
  getPageType?: ((defaults: RuntimeConfigValue) => string) & { defaults?: RuntimeConfigValue };
  typeToSelectorMap?: ConfigMap<string>;
};

export type CategorySelectionPageRule = { pathname?: string; field?: string };

export type PlatformName = 'shift4shop' | 'shopify' | 'bigcommerce' | 'magento';

export type FitmentSearchSubConfig = RuntimeConfigMap &
  FitmentSearchCallbackConfig & {
    autoSyncKey?: string;
    baseFields?: string[];
    categorySelectionPageRedirect?: boolean;
    categorySelectionPages?: CategorySelectionPageRule[] | CategorySelectionPageRule;
    doNotSaveSelectedVehicle?: boolean;
    extraFields?: string[];
    isExtraFieldsAlwaysVisible?: boolean;
    forceSpecifyDialog?: boolean;
    forceVehicleSelection?: boolean;
    forceVehicleSelectionInterval?: number;
    getFitmentSearchTitle?: () => string;
    isAutoRedirectDisabled?: boolean;
    isAutoVehicleSelectionDisabled?: boolean;
    hideVehicleFromUrl?: boolean;
    hideVerifyOnCartItems?: boolean;
    vehicleDataExpires?: number;
  };

export type MainConfig = {
  Widgets: AnyWidget[];
  ColorEngineFeature?: boolean;
  InitFunc?: () => void;
  // uiConfig
  AfterInit?: () => void;
  collapsibleAnimationTime?: number;
  colorGroups?: RuntimeConfigValue;
  colorStripes?: string[][];
  CookieStorage?: boolean;
  customFacetValueFilter?: CustomFacetValueFilter;
  customMessage?: CustomMessageConfig;
  customUriParser?: {
    parsingNeeded?: (pageType: string) => boolean;
    parse?: (pageType: string) => ParsedSearchRequest;
  } & RuntimeConfigMap;
  defaultView?: string;
  disableRequestScroll?: boolean;
  FacetsCollapsedDefault?: boolean;
  FacetsIsTreeSelectedHide?: boolean;
  groupColorsBy?: RuntimeConfigValue;
  infiniteScrollEnabled?: boolean;
  joinedChips?: ConfigMap<string[]>;
  noResultsRedirectTimeout?: number;
  page?: PageConfig & RuntimeConfigMap;
  pagination?: { edgeRange?: number; centralRange?: number; disableDelimiters?: boolean };
  platform?: PlatformName;
  scrollDetection?: { threshold: number; higher: string; lower: string };
  SearchPageBaseUrl?: string;
  searchPageUrl?: string;
  sortFields?: string[];
  stickyHeaderSelector?: string;
  useLocationHash?: boolean;
  // requestConfig
  autocomplete?: {
    order?: AutocompleteTypes[];
    facetValueOrder?: string[];
    fields?: string[];
    requestDefaults?: AutocompleteRequestDefaults;
  } & RuntimeConfigMap;
  doAutocompleteRedirect?: boolean;
  facetResponseHandler?: (response: ServerModel.FacetsResponse) => ServerModel.FacetsResponse;
  addItemsToCart?: (ids: string[], fallback?: PlatformAddItemsToCart | null) => void;
  getIgnoredUriParamNames?: (pageType: string) => string[];
  getInitPreselection?: (pageType: string, defaults?: RuntimeConfigValue) => PreselectionEntry[] | null;
  getLocalPreselection?: (pageType: string, defaults?: RuntimeConfigValue) => PreselectionEntry[] | null;
  getSearchRequestDefaults?: SearchRequestDefaultsFactory;
  isAnalyticsEnabled?: boolean;
  telemetryRate?: number;
  isHtmxMode?: boolean;
  maxCountByField?: ConfigMap<number>;
  pageUriParam?: string;
  requestTimeout?: number;
  responseHandler?: (response: ServerModel.SearchResponse, onInit?: () => void) => ServerModel.SearchResponse;
  SearchRequestDefaults?: SearchRequestDefaults;
  // fitmentSearchConfig
  fitmentSearch?: FitmentSearchSubConfig;
  // productConfig
  product?: ProductRuntimeConfig;
  // facetsConfig
  facets?: ConfigMap;
} & RuntimeConfigMap;

const knownWidgetTypes = new Set<AnyWidget['type']>(widgetTypeNames);

function isAnyWidget(widget: WidgetConfig): widget is WidgetConfig & { type: AnyWidget['type'] } {
  return (
    typeof widget.type === 'string' &&
    typeof widget.name === 'string' &&
    knownWidgetTypes.has(widget.type as AnyWidget['type'])
  );
}

// Filter processed widgets down to known runtime types.
const allProcessedWidgets = widgetProcessor(unprocessedConfig.Widgets as WidgetConfig[]);
const processedWidgets = allProcessedWidgets.filter((widget): widget is WidgetConfig => {
  if (isAnyWidget(widget)) {
    return true;
  }
  logRuntimeError({
    source: 'componentlessWidgetsConfig:unknownWidget',
    error: new Error('dropping widget with unknown type'),
    extra: { 'widget.name': String(widget.name ?? ''), 'widget.type': String(widget.type ?? '') },
  });
  return false;
}) as unknown as AnyWidget[];

export const mainConfig: MainConfig = {
  ...unprocessedConfig,
  Widgets: processedWidgets,
} as MainConfig;

export default processedWidgets;
